// Het vlak Kosten & credits (W6 ronde 4, besluit V1; research §3.5 kosten).
//
// Vijf vragen van boven naar beneden: hoe staan we tegenover het plafond, wat
// kostte een maand per functie en per laan, waar komen de prijzen vandaan, wie
// gebruikt hoeveel krediet, en wat kost een run die je nog wilt draaien.
//
// Alles komt van de server (GET /api/beheer/kosten): elk totaal, de stand en de
// zin van het plafond, de raming en haar oordeel. Dit vlak rekent geen enkel
// bedrag uit. Drie soorten bedrag gaan hier nooit bij elkaar op:
//  · vastgelegd — wat een run zelf bewaarde;
//  · echt — wat OpenRouter per chatvraag meestuurde;
//  · berekend — tokens × de prijs van het model dat de laan vandaag draagt, een
//    BOVENGRENS (runs leggen hun model niet vast; gecachte tokens tellen tegen
//    de volle prijs).
// Wat geen prijs heeft (Firecrawl-credits, Exa-verzoeken) blijft een aantal met
// zijn reden, nooit een bedrag.
//
// Het plafond is een concept (V7): de owner stelt het vast. Het label zegt dat
// op het scherm, zodat niemand een voorstel voor een afspraak aanziet. De zin
// over de projectie komt uit `plafond.tekst` — dit vlak bouwt hem niet na, ook
// niet als er geen plafond is: dan zegt de server dat in dezelfde zin.
//
// Het grote getal is `besteedUsd` van de server: vastgelegd + echt + berekend.
// Berekend is een bovengrens, dus het getal ook — vandaar "ten hoogste".

import { Card, ReasonBadge } from "../../components/ui";
import { EmptyReason, LoadError } from "../../components/Empty";
import { api } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import type { KostenMaand, KostenPlafond, KostenResponse } from "../../contracts";
import { color, sectionLabel, size, weight } from "../../tokens";
import { Blok, cijfer, datum, noot } from "./BronnenStukken";
import { DollarIcoon } from "./Iconen";
import { CreditsPerPersoon } from "./KostenCredits";
import { PerMaand } from "./KostenMaanden";
import { Raming, type LaanKeuze } from "./KostenRaming";
import { maandNaam, usd } from "./KostenStukken";
import { Stoplicht } from "./Stoplicht";
import { prijsloosLabel } from "./woorden";

export function Kosten() {
  const kosten = useResource<KostenResponse>(() => api.kosten(), []);

  if (kosten.error) {
    return (
      <Card style={{ padding: 18 }}>
        <LoadError reason={kosten.error} onRetry={kosten.reload} />
      </Card>
    );
  }
  if (!kosten.data) {
    return (
      <Card style={{ padding: 18 }}>
        <EmptyReason reason="de kosten worden opgehaald" />
      </Card>
    );
  }

  const k = kosten.data;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
      <Plafond plafond={k.plafond} />
      <PerMaand maanden={k.maanden} uitleg={k.maandUitleg} zonderPrijs={k.prijzen.zonderPrijs} />
      <Prijzen prijzen={k.prijzen} />
      <CreditsPerPersoon credits={k.credits} />
      <Raming lanen={lanenMetRuns(k.maanden)} />
      <p style={{ ...noot, marginTop: 0 }}>
        {`Gemeten op ${datum(k.gemetenOp) ?? "een onbekend moment"}.`}
      </p>
    </div>
  );
}

/** De lanen die in een maand runs hadden — alleen daarvoor heeft de server
 *  eerdere runs om een raming op te baseren. Opzoeken, geen rekensom: de
 *  sleutel gaat naar de server, het woord van de server (`laanLabel`) staat in
 *  de keuzelijst, en daarop is ook de volgorde. */
function lanenMetRuns(maanden: readonly KostenMaand[]): LaanKeuze[] {
  const woord = new Map<string, string>();
  for (const m of maanden) {
    for (const l of m.lanen) if (!woord.has(l.laan)) woord.set(l.laan, l.laanLabel);
  }
  return [...woord]
    .map(([laan, laanLabel]) => ({ laan, laanLabel }))
    .sort((a, b) => a.laanLabel.localeCompare(b.laanLabel, "nl"));
}

function Plafond({ plafond: p }: { plafond: KostenPlafond }) {
  return (
    <Blok
      titel={`Plafond · ${maandNaam(p.maand)}`}
      rechts={
        p.concept ? (
          // V7: het bedrag is een voorstel tot de owner het vaststelt.
          <ReasonBadge
            text="concept — de owner stelt het vast"
            tone="amber"
            title="Het plafond is een voorstel. Tot de owner het vaststelt is het een richtpunt, geen afspraak."
          />
        ) : undefined
      }
    >
      <Card style={{ padding: "18px 20px" }}>
        <div data-plafond>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 36, alignItems: "flex-end" }}>
            <div>
              <div
                style={{
                  ...sectionLabel,
                  marginBottom: 6,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {/* Ten hoogste: een deel is berekend tegen de volle prijs
                    (bovengrens); de toelichting van de server eronder zegt hoe. */}
                <DollarIcoon /> Ten hoogste besteed deze maand
              </div>
              <div
                style={{
                  fontSize: 32,
                  fontWeight: weight.bold,
                  color: color.ink,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                  ...cijfer,
                }}
              >
                {usd(p.besteedUsd)}
              </div>
            </div>
            {/* Geen plafond: geen kolom met een eigen zin — de zin van de
                server hieronder zegt het al ("Geen plafond ingesteld."). */}
            {p.plafondUsd !== null && (
              <div>
                <div style={{ ...sectionLabel, marginBottom: 6 }}>Plafond</div>
                <div
                  style={{ fontSize: 20, fontWeight: weight.medium, color: color.body, ...cijfer }}
                >
                  {usd(p.plafondUsd)}
                </div>
              </div>
            )}
          </div>

          {/* De stand én de zin van de server: het oordeel (en een eventuele
              projectie) staat daarin, niet in een eigen percentage. Bij de
              stand "leeg" is die zin ook het woord van het stoplicht; anders
              zou het "niet gemeten" zeggen, en besteed is wél gemeten. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 14,
              fontSize: size.body,
              color: color.ink,
            }}
          >
            <Stoplicht stand={p.stand} reden={p.tekst} />
            <span>{p.tekst}</span>
          </div>
          <p style={noot}>{p.toelichting}</p>
        </div>
      </Card>
    </Blok>
  );
}

function Prijzen({ prijzen }: { prijzen: KostenResponse["prijzen"] }) {
  return (
    <Blok titel="Waar de prijzen vandaan komen">
      <Card style={{ padding: "14px 18px", fontSize: size.meta, color: color.body, lineHeight: 1.55 }}>
        {prijzen.bron ? (
          <p>{`Prijzen per model: ${prijzen.bron}.`}</p>
        ) : (
          <EmptyReason reason="de server noemt geen bron voor de prijzen" />
        )}
        {prijzen.zonderPrijs.length > 0 && (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "10px 0 0",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            {prijzen.zonderPrijs.map((z) => (
              <li key={z.wat} data-zonder-prijs={z.wat}>
                <span style={{ fontWeight: weight.medium, color: color.ink }}>
                  {prijsloosLabel(z.wat)}
                </span>
                {": geen prijs — "}
                {z.reden ?? <EmptyReason reason="de server noemt geen reden" />}
              </li>
            ))}
          </ul>
        )}
        <p style={noot}>
          Wat geen prijs heeft blijft een aantal en telt in geen enkel bedrag mee.
        </p>
      </Card>
    </Blok>
  );
}
